/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useContext, useEffect, useMemo, useCallback } from "react";
import Box from "@mui/material/Box";
import Modal from "@mui/material/Modal";
import Fade from "@mui/material/Fade";
import CustomButton from "../../../components/common/Button/CustomButton";
import AddIcon from "@mui/icons-material/Add";
import SlidersAPI from "../../../api/firebase/SlidersAPI";
import { Chip, Snackbar, Typography, Paper, Stack, Button, Skeleton } from "@mui/material";
import MuiAlert from "@mui/material/Alert";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import RefreshIcon from "@mui/icons-material/Refresh";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import OpenInNewRoundedIcon from "@mui/icons-material/OpenInNewRounded";
import DragIndicatorRoundedIcon from "@mui/icons-material/DragIndicatorRounded";
import AddSlider from "../../../components/admin/add-slider/AddSlider";
import AllSlidersContext from "../../../context/allSliders/AllSlidersContext";
import AddSliderContext from "../../../context/addSlider/AddSliderContext";
import { styles } from "./styles";
import { formatDateTimeForAdmin } from "../../../utils/dateUtils";

const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

const SlidersListAdmin = () => {
  const initState = {
    id: "",
    title: "",
    imageUrl: "",
    imageName: "",
    link: "",
    order: 1,
    isActive: true,
  };

  const slidersAPI = useMemo(() => new SlidersAPI(), []);
  const allSlidersContext = useContext(AllSlidersContext);
  const sliderContext = useContext(AddSliderContext);

  const sliders = Array.isArray(allSlidersContext.state) ? allSlidersContext.state : [];
  const { setState: setSliders } = allSlidersContext;
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [saving, setSaving] = useState(false);
  const [reordering, setReordering] = useState(false);
  const [draggedBannerId, setDraggedBannerId] = useState(null);
  const [dropTargetId, setDropTargetId] = useState(null);
  const fetchSliders = useCallback(async () => {
    setLoading(true);
    setLoadError(false);
    try {
      const res = await slidersAPI.getSliders();
      if (!res?.success) throw new Error("Load failed");
      setSliders([...(res.data || [])].sort((a, b) => (a.order || 0) - (b.order || 0)));
    } catch {
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  }, [slidersAPI, setSliders]);
  useEffect(() => { fetchSliders(); }, [fetchSliders]);


  // Modal State
  const [openSliderModal, setOpenSliderModal] = useState(false);
  const [editSliderModal, setEditSliderModal] = useState(false);

  const showAddSliderModal = (action) => {
    if (action === "ADD") {
      setEditSliderModal(false);
      // Auto assign next order number
      const nextOrder = Math.max(0, ...sliders.map((banner) => Number(banner.order) || 0)) + 1;
      sliderContext.setState({
        ...initState,
        order: nextOrder,
      });
    }
    setOpenSliderModal(true);
  };

  const hideAddSliderModal = () => { if (!saving) setOpenSliderModal(false); };

  // Snackbar State
  const [snackbarState, setSnackbarState] = useState(false);
  const [message, setMessage] = useState({ text: "", type: "success" });

  const showSnackbar = () => setSnackbarState(true);
  const hideSnackbar = (event, reason) => {
    if (reason === "clickaway") return;
    setSnackbarState(false);
  };

  // Set Edit Slider
  const setEditSlider = (slider) => {
    setEditSliderModal(true);
    sliderContext.setState(slider);
    setOpenSliderModal(true);
  };

  // Add / Update Slider
  const handleSetSlider = async (
    action = "ADD",
    slider_id = null,
    payload = null
  ) => {
    if (saving) return;
    setSaving(true);
    const isVisibilityToggle = payload !== null;
    const targetId =
      slider_id ||
      sliderContext.state.id ||
      `SLIDER_${Date.now()}`;

    let dataToSave;
    if (isVisibilityToggle) {
      dataToSave = payload;
    } else {
      dataToSave = {
        title: sliderContext.state.title || "",
        imageUrl: sliderContext.state.imageUrl || "",
        imageName: sliderContext.state.imageName || "",
        link: sliderContext.state.link || "",
        order: Number(sliderContext.state.order) || 1,
        isActive: sliderContext.state.isActive ?? true,
      };
    }

    try {
      const res = await slidersAPI.setSlider({
        id: targetId,
        payload: {
          ...dataToSave,
          ...(action === "ADD" ? { createdAt: { seconds: Math.floor(Date.now() / 1000) } } : {}),
        },
      });

      if (res.success) {
        const actionMsg =
          isVisibilityToggle
            ? "Status updated"
            : action === "ADD"
            ? "Banner added successfully!"
            : "Banner updated successfully!";

        setMessage({ text: actionMsg, type: "success" });
        showSnackbar();

        if (action === "ADD" && !isVisibilityToggle) {
          const newSlider = {
            id: targetId,
            ...dataToSave,
            createdAt: { seconds: Math.floor(Date.now() / 1000) },
          };
          const updated = [...allSlidersContext.state, newSlider].sort(
            (a, b) => (a.order || 0) - (b.order || 0)
          );
          allSlidersContext.setState(updated);
        } else {
          const updated = allSlidersContext.state
            .map((item) => {
              if (item.id === targetId) {
                return { ...item, ...dataToSave };
              }
              return item;
            })
            .sort((a, b) => (a.order || 0) - (b.order || 0));
          allSlidersContext.setState(updated);
        }

        if (!isVisibilityToggle) {
          sliderContext.setState(initState);
          setOpenSliderModal(false);
        }
      } else {
        setMessage({
          text: "Failed to save banner. Please try again.",
          type: "error",
        });
        showSnackbar();
      }
    } catch (err) {
      console.log("Save slider error:", err);
      setMessage({
        text: "Could not save banner.",
        type: "error",
      });
      showSnackbar();
    } finally {
      setSaving(false);
    }
  };

  const handleBannerDrop = async (targetId) => {
    const sourceId = draggedBannerId;
    setDraggedBannerId(null);
    setDropTargetId(null);
    if (!sourceId || sourceId === targetId || reordering || saving) return;

    const previous = [...sliders];
    const sourceIndex = previous.findIndex((banner) => banner.id === sourceId);
    const targetIndex = previous.findIndex((banner) => banner.id === targetId);
    if (sourceIndex < 0 || targetIndex < 0) return;

    const reordered = [...previous];
    const [movedBanner] = reordered.splice(sourceIndex, 1);
    reordered.splice(targetIndex, 0, movedBanner);
    const normalized = reordered.map((banner, index) => ({ ...banner, order: index + 1 }));

    setSliders(normalized);
    setReordering(true);
    try {
      const results = await Promise.all(
        normalized.map((banner) => slidersAPI.setSlider({
          id: banner.id,
          payload: { order: banner.order },
        }, false))
      );
      if (results.some((result) => !result?.success)) throw new Error("Reorder failed");
      setMessage({ text: "Banner order updated!", type: "success" });
      showSnackbar();
    } catch (error) {
      setSliders(previous);
      setMessage({ text: "Could not update banner order. Please try again.", type: "error" });
      showSnackbar();
    } finally {
      setReordering(false);
    }
  };

  return (
    <Box sx={{ pb: 4 }}>
      <Box sx={{ mb: 3, p: { xs: 2.5, sm: 3.5 }, borderRadius: 4, color: "#ffffff", background: "linear-gradient(135deg, #00382e 0%, #004d40 60%, #056152 100%)", boxShadow: "0 10px 25px -5px rgba(0,77,64,0.3)", display: { xs: "none", sm: "flex" }, flexDirection: { xs: "column", md: "row" }, alignItems: { xs: "stretch", md: "center" }, justifyContent: "space-between", gap: 2.5 }}>
        <Box>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}><Box sx={{ p: 0.5, borderRadius: "6px", bgcolor: "rgba(255,255,255,0.15)", display: "flex" }}><ImageOutlinedIcon fontSize="small" /></Box><Typography variant="overline" sx={{ fontWeight: 800, letterSpacing: "0.1em", color: "#a7f3d0", fontSize: "0.75rem" }}>STOREFRONT CONTENT</Typography></Stack>
          <Typography component="h1" variant="h4" sx={{ fontWeight: 800, fontSize: { xs: "1.75rem", sm: "2.2rem" }, letterSpacing: "-0.02em" }}>Banner Images</Typography>
          <Typography variant="body2" sx={{ opacity: 0.88, mt: 0.5, maxWidth: 520 }}>Manage homepage banners, display order, destination links, and storefront visibility.</Typography>
        </Box>
        <Stack direction="row" spacing={1.5}>
          <Button onClick={fetchSliders} disabled={loading || saving} startIcon={<RefreshIcon />} sx={{ color: "#ffffff", bgcolor: "rgba(255,255,255,0.15)", borderRadius: "10px", textTransform: "none", "&:hover": { bgcolor: "rgba(255,255,255,0.25)" } }}>{loading ? "Loading…" : "Refresh"}</Button>
          <CustomButton variant="contained" startIcon={<AddIcon />} disabled={loading || loadError} onClick={() => showAddSliderModal("ADD")} sx={{ fontWeight: 800, bgcolor: "#ffffff", color: "#004d40", px: 3, py: 1.2, borderRadius: "10px", boxShadow: "0 4px 14px rgba(0,0,0,0.15)", "&:hover": { bgcolor: "#f8fafc", color: "#00382e" } }}>Add Banner Image</CustomButton>
        </Stack>
      </Box>
      {loadError && <MuiAlert severity="error" sx={{ mb: 2, borderRadius: 2 }} action={<Button color="inherit" onClick={fetchSliders}>Retry</Button>}>Could not load banner images. Please try again.</MuiAlert>}

      {/* Snackbar alerts */}
      <Snackbar
        open={snackbarState}
        autoHideDuration={4000}
        onClose={hideSnackbar}
      >
        <Alert
          onClose={hideSnackbar}
          severity={message.type === "success" ? "success" : "error"}
          sx={{ width: "100%" }}
        >
          {message.text}
        </Alert>
      </Snackbar>

      {/* Add / Edit Slider Modal */}
      <Modal
        aria-labelledby="slider-modal-title"
        aria-describedby="slider-modal-description"
        open={openSliderModal}
        onClose={hideAddSliderModal}
        closeAfterTransition
      >
        <Fade in={openSliderModal}>
          <Box sx={styles.boxStyle}>
            <AddSlider
              hideModal={hideAddSliderModal}
              handleSetSlider={handleSetSlider}
              saving={saving}
              action={editSliderModal ? "EDIT" : "ADD"}
            />
          </Box>
        </Fade>
      </Modal>

      <Typography variant="caption" sx={{ color: "#64748b" }}>
        {loading ? "Loading banners…" : loadError ? "Banner data unavailable" : `${sliders.length} banners • Lower display orders appear first`}
      </Typography>

      {/* Banner card gallery */}
      <Box sx={{ width: "100%", mt: 2 }}>
        {loading ? <Skeleton variant="rounded" height={360} /> : loadError ? null : sliders.length === 0 ? <Paper elevation={0} sx={{ p: 6, textAlign: "center", border: "1px solid #e2e8f0", borderRadius: 3 }}><ImageOutlinedIcon sx={{ fontSize: 44, color: "#94a3b8", mb: 1 }} /><Typography variant="h6" sx={{ fontWeight: 800, color: "#1e293b" }}>No banners yet</Typography><Typography variant="body2" sx={{ color: "#64748b", mt: 1 }}>Add your first banner to personalize the homepage carousel.</Typography></Paper> : (
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 320px), 1fr))",
              gap: 2,
            }}
          >
            {sliders.map((banner, index) => (
              <Paper
                component="article"
                key={banner.id}
                aria-label={banner.title || "Untitled banner"}
                draggable={!reordering && !saving}
                onDragStart={(event) => {
                  if (reordering || saving) return;
                  event.dataTransfer.effectAllowed = "move";
                  event.dataTransfer.setData("text/plain", banner.id);
                  setDraggedBannerId(banner.id);
                }}
                onDragOver={(event) => {
                  if (!draggedBannerId || draggedBannerId === banner.id) return;
                  event.preventDefault();
                  event.dataTransfer.dropEffect = "move";
                  setDropTargetId(banner.id);
                }}
                onDragLeave={(event) => {
                  if (!event.currentTarget.contains(event.relatedTarget)) setDropTargetId(null);
                }}
                onDrop={(event) => {
                  event.preventDefault();
                  handleBannerDrop(banner.id);
                }}
                onDragEnd={() => {
                  setDraggedBannerId(null);
                  setDropTargetId(null);
                }}
                elevation={0}
                sx={{
                  minWidth: 0,
                  overflow: "hidden",
                  border: "1px solid #e2e8f0",
                  borderRadius: "8px",
                  bgcolor: "#ffffff",
                  boxShadow: "0 4px 14px rgba(15,23,42,0.05)",
                  opacity: draggedBannerId === banner.id ? 0.5 : 1,
                  outline: dropTargetId === banner.id ? "3px solid #34d399" : "3px solid transparent",
                  outlineOffset: 2,
                  cursor: reordering ? "wait" : "grab",
                  animation: "bannerCardIn 440ms cubic-bezier(0.16, 1, 0.3, 1) backwards",
                  animationDelay: `${index * 70}ms`,
                  transition: "border-color .18s ease, box-shadow .18s ease, transform .18s ease",
                  "&:hover": {
                    borderColor: "#86b9aa",
                    boxShadow: "0 14px 30px rgba(15,23,42,0.1)",
                    transform: "translateY(-3px)",
                  },
                  "@keyframes bannerCardIn": {
                    from: { opacity: 0, transform: "translateY(10px)" },
                    to: { opacity: 1, transform: "translateY(0)" },
                  },
                  "@media (prefers-reduced-motion: reduce)": { animation: "none", transition: "none" },
                }}
              >
                <Box sx={{ position: "relative", aspectRatio: "16 / 7", bgcolor: "#f1f5f9", overflow: "hidden" }}>
                  {banner.imageUrl ? (
                    <Box
                      component="img"
                      src={banner.imageUrl}
                      alt={banner.title || "Homepage banner"}
                      sx={{ width: "100%", height: "100%", display: "block", objectFit: "cover" }}
                    />
                  ) : (
                    <Box sx={{ width: "100%", height: "100%", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", color: "#94a3b8" }}>
                      <ImageOutlinedIcon sx={{ fontSize: 34 }} />
                      <Typography variant="caption" sx={{ mt: 0.5 }}>No banner image</Typography>
                    </Box>
                  )}
                  <Chip
                    label={banner.isActive ? "Active" : "Hidden"}
                    size="small"
                    sx={{
                      position: "absolute",
                      top: 10,
                      left: 10,
                      height: 24,
                      bgcolor: banner.isActive ? "#dcfce7" : "#f1f5f9",
                      color: banner.isActive ? "#166534" : "#475569",
                      border: `1px solid ${banner.isActive ? "#86efac" : "#cbd5e1"}`,
                      fontWeight: 800,
                    }}
                  />
                  <Box sx={{ position: "absolute", top: 10, right: 10, minWidth: 34, height: 24, px: 1, display: "inline-flex", alignItems: "center", justifyContent: "center", borderRadius: "6px", bgcolor: "rgba(15,23,42,.78)", color: "#ffffff", fontSize: 11, fontWeight: 800 }}>
                    #{banner.order ?? 1}
                  </Box>
                  <Box
                    aria-label={`Drag ${banner.title || "untitled banner"} to reorder`}
                    title="Drag to reorder"
                    sx={{ position: "absolute", bottom: 10, right: 10, width: 30, height: 30, display: "inline-flex", alignItems: "center", justifyContent: "center", borderRadius: "7px", bgcolor: "rgba(255,255,255,.92)", color: "#334155", boxShadow: "0 2px 8px rgba(15,23,42,.18)", cursor: reordering ? "wait" : "grab" }}
                  >
                    <DragIndicatorRoundedIcon sx={{ fontSize: 19 }} />
                  </Box>
                </Box>

                <Box sx={{ p: 2 }}>
                  <Typography component="h2" variant="subtitle1" title={banner.title || "Untitled banner"} sx={{ color: "#1e293b", fontWeight: 800, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {banner.title || "Untitled banner"}
                  </Typography>
                  <Typography variant="caption" sx={{ display: "block", mt: 0.25, color: "#64748b" }}>
                    {banner.createdAt ? `Created ${formatDateTimeForAdmin(banner.createdAt)}` : "Creation date unavailable"}
                  </Typography>

                  <Box sx={{ mt: 1.75, pt: 1.5, borderTop: "1px solid #e2e8f0", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1.5 }}>
                    {banner.link ? (
                      <Button
                        component="a"
                        href={banner.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        size="small"
                        endIcon={<OpenInNewRoundedIcon />}
                        sx={{ minWidth: 0, maxWidth: "65%", justifyContent: "flex-start", color: "#17664f", textTransform: "none", fontSize: 11, "& .MuiButton-endIcon": { flexShrink: 0 }, "& .MuiButton-startIcon": { flexShrink: 0 } }}
                      >
                        <Box component="span" sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{banner.link}</Box>
                      </Button>
                    ) : (
                      <Typography variant="caption" sx={{ color: "#94a3b8" }}>No destination link</Typography>
                    )}
                    <Button
                      type="button"
                      size="small"
                      variant="outlined"
                      startIcon={<EditOutlinedIcon />}
                      onClick={() => setEditSlider(banner)}
                      aria-label={`Edit ${banner.title || "untitled banner"}`}
                      sx={{ flexShrink: 0, borderColor: "#b7cec6", color: "#17664f", borderRadius: "7px", textTransform: "none", fontWeight: 700, "&:hover": { borderColor: "#17664f", bgcolor: "#f0f7f4" } }}
                    >
                      Edit
                    </Button>
                  </Box>
                </Box>
              </Paper>
            ))}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default SlidersListAdmin;
